import { supabase } from '../lib/supabase';
import { isDemoModeActive } from '../contexts/DemoContext';
import type { ProjectActivityLog } from '../types/team';

/**
 * プロジェクトの活動ログを取得
 * @param projectId プロジェクトID
 * @param limit 取得件数（デフォルト: 50）
 * @returns 活動ログの配列
 */
export const getProjectActivityLogs = async (
  projectId: string,
  limit = 50
): Promise<ProjectActivityLog[]> => {
  // デモモード中はDB呼び出しをスキップ
  if (isDemoModeActive()) {
    return [];
  }

  try {
    // 活動ログを取得
    const { data: logs, error } = await supabase
      .from('project_activity_logs')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching activity logs:', error);
      throw error;
    }

    if (!logs || logs.length === 0) {
      return [];
    }

    // ユーザーIDを収集（重複排除）
    const userIds = [...new Set(logs.map((log) => log.user_id))];

    // 割り当てられたユーザーIDも収集
    const assignedUserIds = logs
      .filter((log) => log.details?.assigned_to)
      .map((log) => log.details!.assigned_to as string);
    const allUserIds = [...new Set([...userIds, ...assignedUserIds])];

    // ユーザープロフィールを取得
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, nickname, avatar_url')
      .in('id', allUserIds);

    // プロフィール情報をマップに変換
    const profileMap = new Map((profiles || []).map((p) => [p.id, p]));

    // ログにプロフィール情報を結合
    return logs.map((log) => ({
      ...log,
      user_nickname: profileMap.get(log.user_id)?.nickname || '不明なユーザー',
      user_avatar_url: profileMap.get(log.user_id)?.avatar_url,
      assigned_user_nickname: log.details?.assigned_to
        ? profileMap.get(log.details.assigned_to as string)?.nickname
        : undefined,
    })) as ProjectActivityLog[];
  } catch (error) {
    console.error('Error in getProjectActivityLogs:', error);
    throw error;
  }
};

/**
 * 手動で活動ログを追加
 * @param log 活動ログデータ
 */
export const createActivityLog = async (
  log: Omit<ProjectActivityLog, 'id' | 'created_at'>
): Promise<void> => {
  try {
    const { error } = await supabase.from('project_activity_logs').insert({
      project_id: log.project_id,
      user_id: log.user_id,
      action_type: log.action_type,
      target_type: log.target_type,
      target_id: log.target_id,
      details: log.details,
    });

    if (error) {
      console.error('Error creating activity log:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in createActivityLog:', error);
    throw error;
  }
};

/**
 * 活動ログを削除（必要に応じて）
 * @param logId ログID
 */
export const deleteActivityLog = async (logId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('project_activity_logs')
      .delete()
      .eq('id', logId);

    if (error) {
      console.error('Error deleting activity log:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteActivityLog:', error);
    throw error;
  }
};

/**
 * 特定のユーザーの活動ログを取得
 * @param projectId プロジェクトID
 * @param userId ユーザーID
 * @param limit 取得件数
 */
export const getUserActivityLogs = async (
  projectId: string,
  userId: string,
  limit = 20
): Promise<ProjectActivityLog[]> => {
  try {
    const { data: logs, error } = await supabase
      .from('project_activity_logs')
      .select('*')
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching user activity logs:', error);
      throw error;
    }

    if (!logs || logs.length === 0) {
      return [];
    }

    // ユーザープロフィールを取得
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('id, nickname, avatar_url')
      .eq('id', userId)
      .single();

    return logs.map((log) => ({
      ...log,
      user_nickname: profile?.nickname || '不明なユーザー',
      user_avatar_url: profile?.avatar_url,
    })) as ProjectActivityLog[];
  } catch (error) {
    console.error('Error in getUserActivityLogs:', error);
    throw error;
  }
};

/**
 * 特定のタスクの活動ログを取得
 * @param taskId タスクID
 * @param limit 取得件数
 */
export const getTaskActivityLogs = async (
  taskId: string,
  limit = 20
): Promise<ProjectActivityLog[]> => {
  try {
    const { data: logs, error } = await supabase
      .from('project_activity_logs')
      .select('*')
      .eq('target_id', taskId)
      .eq('target_type', 'task')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching task activity logs:', error);
      throw error;
    }

    if (!logs || logs.length === 0) {
      return [];
    }

    // ユーザーIDを収集
    const userIds = [...new Set(logs.map((log) => log.user_id))];

    // ユーザープロフィールを取得
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, nickname, avatar_url')
      .in('id', userIds);

    const profileMap = new Map((profiles || []).map((p) => [p.id, p]));

    return logs.map((log) => ({
      ...log,
      user_nickname: profileMap.get(log.user_id)?.nickname || '不明なユーザー',
      user_avatar_url: profileMap.get(log.user_id)?.avatar_url,
    })) as ProjectActivityLog[];
  } catch (error) {
    console.error('Error in getTaskActivityLogs:', error);
    throw error;
  }
};
